import React, { useState, useEffect } from 'react';
import { useGame, api } from '../context/GameContext';
import { Trophy, X, ExternalLink, Star, Sparkles, Users, Layers } from 'lucide-react';
import ChessRook from '../components/ChessRook';
import { SetBadges } from '../components/CollectionSetBadges';
import SectionLoader from '../components/SectionLoader';
import TonSparkles from '../components/TonSparkles';
import thumb from '../utils/thumb';

const RARITY_COLORS = {
  common: '#9CA3AF', uncommon: '#10B981', rare: '#3B82F6',
  legendary: '#A855F7', masterpiece: '#F59E0B',
};

// Gold / silver / bronze treatment for the first three collections.
const PODIUM = [
  { ring: '#F5D07A', glow: 'rgba(245,208,122,0.30)' },
  { ring: '#D8DEE9', glow: 'rgba(216,222,233,0.22)' },
  { ring: '#C98B5B', glow: 'rgba(201,139,91,0.22)' },
];


const RARITY_LABELS = {
  common: { en: 'Common', ru: 'Common' },
  uncommon: { en: 'Uncommon', ru: 'Uncommon' },
  rare: { en: 'Rare', ru: 'Rare' },
  legendary: { en: 'Legendary', ru: 'Legendary' },
  masterpiece: { en: 'Masterpiece', ru: 'Masterpiece' },
};

const getInfluenceLevel = (xp) => {
  let level = 1, totalNeeded = 0;
  while (true) {
    const cost = level * 2 + level * level;
    if (xp < totalNeeded + cost) break;
    totalNeeded += cost;
    level++;
  }
  return level;
};
const getInfluenceXpForLevel = (level) => {
  let total = 0;
  for (let l = 1; l < level; l++) total += l * 2 + l * l;
  return total;
};
const getInfluenceNextCost = (level) => level * 2 + level * level;

/* ======= Preview Modal — matches Collection card style ======= */
const PaintingPreviewModal = ({ card, language, t, onClose }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);
  const handleClose = () => { setVisible(false); setTimeout(onClose, 300); };

  const durability = card.durability || 100;
  const level = getInfluenceLevel(card.influence_xp || 0);
  const levelBase = getInfluenceXpForLevel(level);
  const nextCost = getInfluenceNextCost(level);
  const xpProgress = nextCost > 0 ? ((card.influence_xp - levelBase) / nextCost) * 100 : 0;
  const rarityColor = RARITY_COLORS[card.rarity] || '#9CA3AF';
  const label = RARITY_LABELS[card.rarity]?.[language] || RARITY_LABELS[card.rarity]?.en || card.rarity;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center"
      style={{ transition: 'background 0.3s', background: visible ? 'rgba(0,0,0,0.7)' : 'transparent' }}
      onClick={handleClose}
    >
      <div
        className="w-full max-w-md bg-[#12121C] rounded-t-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        style={{
          transition: 'transform 0.3s ease-out, opacity 0.3s',
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          opacity: visible ? 1 : 0,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white transition-colors"
          data-testid="preview-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Image */}
        <div className="relative">
          <img
            src={thumb(card.image_url, 320)}
            alt={card.title}
            className="w-full aspect-square object-cover rounded-t-2xl"
            style={{
              objectPosition: `${card.image_focus_x ?? 50}% ${card.image_focus_y ?? 50}%`,
            }}
          />
          {card.is_unique && (
            <div
              data-testid="top-modal-unique-badge"
              title={language === 'ru' ? 'Единственный экземпляр' : 'One of a kind'}
              className="absolute top-3 left-3 z-10 px-2 py-1 rounded-md text-[10px] font-bold tracking-[0.12em]"
              style={{
                background: 'linear-gradient(135deg,#FACC15,#F59E0B)',
                color: '#0a0a12',
                boxShadow: '0 4px 12px rgba(250,204,21,0.4)',
              }}
            >
              UNIQUE
            </div>
          )}
        </div>

        {/* Details section — same as Collection card */}
        <div className="p-3.5">
          {/* Condition color bar */}
          <div className="h-0.5 rounded-full mb-2.5 bg-[#1a1a2e]">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${durability}%`, backgroundColor: rarityColor }} />
          </div>

          {/* Title + # + Level */}
          <div className="flex items-start justify-between mb-2">
            <div className="min-w-0 flex-1 mr-2">
              <h2 className="font-heading text-base font-medium text-[#F5F5F7] truncate">
                {card.title} <span className="font-mono text-xs" style={{ color: rarityColor }}>#{card.instance_number || ''}</span>
              </h2>
              <p className="text-xs text-[#A1A1AA] truncate">{card.artist}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-0.5 px-2 py-1 rounded-full bg-[#D4AF37]/10">
                <ChessRook className="w-3 h-3 text-[#D4AF37]" />
                <span className="font-mono text-sm text-[#D4AF37]">{level}</span>
              </div>
              <div
                className="flex items-center gap-0.5 px-2 py-1 rounded-full bg-[#FACC15]/10"
                title={language === 'ru' ? 'Мин. ценность' : 'Min. value'}
                data-testid={card.auction_won_amount > 0 ? "top-auction-price-badge" : undefined}
              >
                <Star className="w-3 h-3 text-[#FACC15] fill-[#FACC15] animate-star-pulse" />
                <span className="font-mono text-sm text-[#FACC15]">
                  {Math.max(card.auction_won_amount || 0, level * 10)}
                </span>
              </div>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-1.5 mb-2">
            <div className="glass-card p-2 rounded-lg">
              <p className="text-[9px] text-[#A1A1AA] mb-0.5">{language === 'ru' ? 'Уровень' : 'Level'}</p>
              <p className="text-[10px] font-medium" style={{ color: rarityColor }}>{label}</p>
            </div>
            <div className="glass-card p-2 rounded-lg">
              <p className="text-[9px] text-[#A1A1AA] mb-0.5">Supply</p>
              <p className="font-mono text-[10px] text-[#F5F5F7]">{Math.max(1, card.rarity_supply || 0).toLocaleString()}</p>
            </div>
            <div className="glass-card p-2 rounded-lg">
              <p className="text-[9px] text-[#A1A1AA] mb-0.5">{t('year')}</p>
              <p className="font-mono text-xs text-[#F5F5F7]">{card.year || ''}</p>
            </div>
            <div className="glass-card p-2 rounded-lg">
              <p className="text-[9px] text-[#A1A1AA] mb-0.5">{t('owner')}</p>
              <p className="text-[10px] text-[#D4AF37] truncate">@{card.owner_username || '—'}</p>
            </div>
          </div>

          {/* View Post */}
          {card.post_url && (
            <a href={card.post_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 py-1.5 mb-2 rounded-lg glass-card border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors">
              <ExternalLink className="w-3 h-3 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs">{t('view_post')}</span>
            </a>
          )}

          {/* Influence bar */}
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] text-[#A1A1AA]">{language === 'ru' ? 'Влияние' : 'Influence'}</span>
              <span className="font-mono text-[10px] text-[#D4AF37] flex items-center gap-1">
                <ChessRook className="w-2.5 h-2.5 text-[#D4AF37]" />
                Lv.{level}
                <span className="text-[#52525B] ml-0.5">{(card.influence_xp || 0) - levelBase}/{nextCost}</span>
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-[#1a1a2e] overflow-hidden">
              <div className="h-full rounded-full bg-[#D4AF37] transition-all" style={{ width: `${Math.min(xpProgress, 100)}%` }} />
            </div>
          </div>

          {/* Condition bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] text-[#A1A1AA]">{language === 'ru' ? 'Состояние' : 'Condition'}</span>
              <span className="font-mono text-[10px]" style={{ color: durability > 50 ? '#10B981' : durability > 20 ? '#F59E0B' : '#EF4444' }}>
                {durability.toFixed(1)}%
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-[#1a1a2e] overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{
                width: `${durability}%`,
                backgroundColor: durability > 50 ? '#10B981' : durability > 20 ? '#F59E0B' : '#EF4444',
              }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ======= Top Page ======= */
const TopPage = () => {
  const { user, language, t, consumePrefetch } = useGame();

  // Boot-time prefetch: GameContext fired the top + leaderboard
  // requests in parallel with auth so the first paint of this tab is
  // already populated. `consumePrefetch` is single-use; subsequent
  // visits use the fresh fetch fired by the mount-time useEffect.
  const bootTop = React.useMemo(
    () => (consumePrefetch ? consumePrefetch('/gallery/top') : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const bootLeaderboard = React.useMemo(
    () => (consumePrefetch ? consumePrefetch('/leaderboard?limit=100&fresh=1') : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const bootMyRank = React.useMemo(
    () => (consumePrefetch ? consumePrefetch('/leaderboard/me') : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Collections lead the board — they are the end-game flex.
  const [tab, setTab] = useState('collections'); // 'collections' | 'paintings' | 'players'
  const [paintings, setPaintings] = useState(bootTop || []);
  const [collections, setCollections] = useState([]);
  const [players, setPlayers] = useState(bootLeaderboard?.leaderboard || []);
  const [myRank, setMyRank] = useState(bootMyRank || null);
  const [loading, setLoading] = useState(!bootTop && !bootLeaderboard);
  const [progress, setProgress] = useState(0);
  // Per-tab readiness: the collections list is never part of the boot
  // prefetch, so without this the tab flashed its "no collections yet"
  // empty state before the request came back.
  const [loadedTabs, setLoadedTabs] = useState({
    paintings: !!bootTop,
    players: !!bootLeaderboard,
    collections: false,
  });
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    // Force-fresh on every tab visit so a recent rating change a
    // player just made elsewhere shows immediately when they open Top.
    const mark = (key) => setLoadedTabs((prev) => ({ ...prev, [key]: true }));
    const requests = [
      api.get('/gallery/top').then(res => setPaintings(res.data || [])).catch(console.error).finally(() => mark('paintings')),
      api.get('/collections/top').then(res => setCollections(res.data || [])).catch(console.error).finally(() => mark('collections')),
      api.get('/leaderboard?limit=100&fresh=1').then(res => setPlayers(res.data?.leaderboard || [])).catch(console.error).finally(() => mark('players')),
      api.get('/leaderboard/me').then(res => setMyRank(res.data || null)).catch(() => {}),
    ];
    let done = 0;
    requests.forEach((p) => p.finally(() => {
      done += 1;
      setProgress(Math.round(100 * done / requests.length));
    }));
    Promise.all(requests).finally(() => setLoading(false));
  }, []);

  const list = tab === 'collections' ? collections : tab === 'players' ? players : paintings;
  const emptyMsg = tab === 'collections'
    ? (language === 'ru' ? 'Коллекций пока нет. Создай первую в галерее!' : 'No collections yet. Create the first one in your gallery!')
    : tab === 'players'
      ? (language === 'ru' ? 'Пока нет коллекционеров. Будь первым!' : 'No collectors yet. Be the first!')
      : (t('no_influence_paintings') || 'No paintings with influence yet. Start exhibiting!');

  return (
    <div className="min-h-screen pb-24 px-4" data-testid="top-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 pt-3 pb-2 bg-[#05050A]/95 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#D4AF37]" />
            <h1 className="text-lg font-heading text-[#F5F5F7]">
              {language === 'ru' ? 'Топ' : 'Top'}
            </h1>
          </div>
          <div className="flex items-center gap-1.5 bg-[#1a1a2e] px-3 py-1 rounded-full">
            <ChessRook className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="font-mono text-xs text-[#D4AF37]">{user?.total_rating || 0}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl bg-[#0a0a12]">
          <button
            data-testid="tab-collections"
            onClick={() => setTab('collections')}
            className={`flex-1 py-1.5 px-2 rounded-lg text-[11px] font-medium transition-all flex items-center justify-center gap-1 ${
              tab === 'collections'
                ? 'bg-[#1a1a2e] text-[#F5F5F7] shadow-sm'
                : 'text-[#A1A1AA] hover:text-[#F5F5F7]'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            {language === 'ru' ? 'Коллекции' : 'Collections'}
          </button>
          <button
            data-testid="tab-paintings"
            onClick={() => setTab('paintings')}
            className={`flex-1 py-1.5 px-2 rounded-lg text-[11px] font-medium transition-all flex items-center justify-center gap-1 ${
              tab === 'paintings'
                ? 'bg-[#1a1a2e] text-[#F5F5F7] shadow-sm'
                : 'text-[#A1A1AA] hover:text-[#F5F5F7]'
            }`}
          >
            <Trophy className="w-3.5 h-3.5" />
            {language === 'ru' ? 'Картины' : 'Paintings'}
          </button>
          <button
            data-testid="tab-players"
            onClick={() => setTab('players')}
            className={`flex-1 py-1.5 px-2 rounded-lg text-[11px] font-medium transition-all flex items-center justify-center gap-1 ${
              tab === 'players'
                ? 'bg-[#1a1a2e] text-[#F5F5F7] shadow-sm'
                : 'text-[#A1A1AA] hover:text-[#F5F5F7]'
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            {language === 'ru' ? 'Игроки' : 'Players'}
          </button>
        </div>
      </div>

      {loading || !loadedTabs[tab] ? (
        <SectionLoader progress={progress} language={language} />
      ) : list.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          {tab === 'collections'
            ? <Layers className="w-12 h-12 text-[#52525B] mb-3" />
            : <Trophy className="w-12 h-12 text-[#52525B] mb-3" />}
          <p className="text-[#A1A1AA] text-sm">{emptyMsg}</p>
        </div>
      ) : (
        <div className="space-y-2 mt-2">
          {tab === 'collections' ? (
            list.map((coll, idx) => {
              const podium = PODIUM[idx];
              const hero = (coll.cards || []).reduce(
                (best, c) => ((c.rating || 0) > (best?.rating || 0) ? c : best),
                (coll.cards || [])[0],
              );
              return (
              <div
                key={coll.id}
                data-testid={`top-collection-${idx}`}
                className="glass-card rounded-xl p-3 relative overflow-hidden"
                style={podium ? {
                  border: `1px solid ${podium.ring}66`,
                  boxShadow: `0 0 22px ${podium.glow}`,
                } : undefined}
              >
                {podium && hero?.image_url && (
                  <>
                    {/* Blurred hero painting + gradient veil: the podium
                        rows get a sense of place without hurting text
                        contrast. */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        backgroundImage: `url(${hero.image_url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center 25%',
                        filter: 'blur(22px) saturate(1.3)',
                        transform: 'scale(1.25)',
                        opacity: 0.55,
                      }}
                      data-testid={`top-collection-hero-${idx}`}
                    />
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: `linear-gradient(135deg, ${podium.glow} 0%, rgba(8,8,14,0.72) 45%, rgba(8,8,14,0.9) 100%)`,
                      }}
                    />
                  </>
                )}
                <div className="relative">
                <div className="flex items-center gap-3">
                  {coll.owner_photo_url ? (
                    <img
                      src={coll.owner_photo_url}
                      alt=""
                      loading="lazy"
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                      style={{ border: `1px solid ${podium ? podium.ring : 'rgba(212,175,55,0.45)'}` }}
                      data-testid={`top-collection-avatar-${idx}`}
                    />
                  ) : (
                    <div
                      className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-semibold text-[#D4AF37] bg-[#D4AF37]/10"
                      style={{ border: `1px solid ${podium ? podium.ring : 'rgba(212,175,55,0.3)'}` }}
                      data-testid={`top-collection-avatar-${idx}`}
                    >
                      {(coll.owner_username || '?').slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#F5F5F7] truncate">{coll.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className={`text-[10px] truncate ${podium ? 'text-[#C4C4CC]' : 'text-[#52525B]'}`}>
                        @{coll.owner_username || '???'}
                      </p>
                      <span className="text-[10px] font-mono text-[#A1A1AA]">
                        {coll.cards_count}/{coll.slots}
                      </span>
                      {coll.minted_count > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] font-mono text-[#0098EA]">
                          <Sparkles className="w-2.5 h-2.5" />{coll.minted_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <ChessRook className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="font-mono text-sm text-[#D4AF37]">{coll.total_influence}</span>
                  </div>
                </div>

                {coll.best_bonus > 0 && (
                  <div className="mt-2" data-testid={`top-collection-bonus-${idx}`}>
                    <SetBadges sets={coll.sets} language={language} limit={3} />
                  </div>
                )}

                {/* Horizontally scrollable covers. The px/pt padding is
                    what keeps the corner badges (UNIQUE, TON) from being
                    clipped by the scroll container. */}
                <div className="flex gap-2 overflow-x-auto no-scrollbar mt-2.5 px-1.5 pt-2 pb-1">
                  {(coll.cards || []).map((card) => (
                    <div key={card.id} className="relative flex-shrink-0">
                      <img
                        src={thumb(card.image_url, 320)}
                        alt={card.title}
                        loading="lazy"
                        decoding="async"
                        onClick={() => setSelectedCard(card)}
                        className="w-[68px] h-[90px] object-cover rounded-md border cursor-pointer active:scale-95 transition-transform"
                        style={{ borderColor: `${RARITY_COLORS[card.rarity] || '#9CA3AF'}55` }}
                      />
                      {card.nft_address && (
                        <div
                          className="absolute -top-1.5 -left-1.5 w-4 h-4 rounded-full flex items-center justify-center shadow-lg"
                          style={{ background: 'linear-gradient(135deg,#0098EA,#0073C7)', boxShadow: '0 0 6px rgba(0,152,234,0.6)' }}
                        >
                          <Sparkles className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                      {card.is_unique && (
                        <div
                          title={language === 'ru' ? 'Единственный экземпляр' : 'One of a kind'}
                          className="absolute -top-1.5 -right-1.5 px-1 rounded text-[8px] font-bold"
                          style={{
                            background: 'linear-gradient(135deg,#FACC15,#F59E0B)',
                            color: '#0a0a12',
                            boxShadow: '0 0 6px rgba(250,204,21,0.5)',
                            letterSpacing: '0.08em',
                            lineHeight: '1.5',
                          }}
                        >
                          U
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                </div>
              </div>
              );
            })
          ) : tab === 'players' ? (
            <>
              {/* Sticky "me" row pinned at top if I'm outside the top-100 */}
              {myRank && myRank.rank > players.length && (
                <div
                  data-testid="players-me-row"
                  className="glass-card rounded-xl p-3 mb-1 border border-[#D4AF37]/30"
                  style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(212,175,55,0.02))' }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center font-mono text-xs font-bold flex-shrink-0">
                      {myRank.rank}
                    </div>
                    {myRank.photo_url ? (
                      <img src={myRank.photo_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#1a1a2e] flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-[#52525B]" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#F5F5F7] truncate">@{myRank.username || 'you'}</p>
                      <p className="text-[10px] text-[#D4AF37]">{language === 'ru' ? 'Ты' : 'You'}</p>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0">
                      <div className="flex items-center gap-1">
                        <ChessRook className="w-3.5 h-3.5 text-[#D4AF37]" />
                        <span className="font-mono text-sm text-[#D4AF37]">{myRank.total_rating || 0}</span>
                      </div>
                      <span className="text-[9px] text-[#52525B] mt-0.5">
                        {(myRank.cards_count || 0)} {(t('cards') || 'Paintings').toLowerCase()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {players.map((p, idx) => {
                const isMe = p.user_id === user?.id;
                return (
                  <div
                    key={p.user_id}
                    data-testid={`top-player-${idx}`}
                    className="glass-card rounded-xl p-3 relative transition-transform"
                    style={isMe
                      ? { border: '1px solid rgba(212,175,55,0.4)', boxShadow: '0 0 14px rgba(212,175,55,0.15)' }
                      : idx < 3 ? { boxShadow: '0 0 14px rgba(212,175,55,0.12)' } : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-mono text-sm font-bold ${
                        idx === 0 ? 'bg-[#D4AF37]/20 text-[#D4AF37]'
                        : idx === 1 ? 'bg-[#C0C0C0]/20 text-[#C0C0C0]'
                        : idx === 2 ? 'bg-[#CD7F32]/20 text-[#CD7F32]'
                        : 'bg-[#1a1a2e] text-[#52525B]'
                      }`}>{p.rank}</div>
                      {p.photo_url ? (
                        <img src={p.photo_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#1a1a2e] flex items-center justify-center flex-shrink-0">
                          <Users className="w-5 h-5 text-[#52525B]" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#F5F5F7] truncate">
                          @{p.username || '???'}
                          {isMe && <span className="ml-1.5 text-[9px] font-mono text-[#D4AF37]">({language === 'ru' ? 'ты' : 'you'})</span>}
                        </p>
                        <p className="text-[10px] text-[#A1A1AA]">
                          {(p.cards_count || 0)} {(t('cards') || 'Paintings').toLowerCase()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <ChessRook className="w-3.5 h-3.5 text-[#D4AF37]" />
                        <span className="font-mono text-sm text-[#D4AF37]">{p.total_rating || 0}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : list.map((card, idx) => {
            const level = getInfluenceLevel(card.influence_xp || 0);
            const durability = card.durability || 100;
            const rarityColor = RARITY_COLORS[card.rarity] || '#9CA3AF';
            const label = RARITY_LABELS[card.rarity]?.[language] || RARITY_LABELS[card.rarity]?.en || card.rarity;
            const isOwn = card.owner_id === user?.id;
            return (
              <div key={card.id} data-testid={`top-card-${idx}`}
                className="glass-card rounded-xl p-3.5 relative cursor-pointer active:scale-[0.98] transition-transform"
                style={idx < 3 ? { boxShadow: `0 0 14px ${rarityColor}25` } : undefined}
                onClick={() => setSelectedCard(card)}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-mono text-sm font-bold ${
                    idx === 0 ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : idx === 1 ? 'bg-[#C0C0C0]/20 text-[#C0C0C0]' : idx === 2 ? 'bg-[#CD7F32]/20 text-[#CD7F32]' : 'bg-[#1a1a2e] text-[#52525B]'
                  }`}>{idx + 1}</div>
                  <div className="relative flex-shrink-0">
                    <div className="relative w-16 h-16 rounded-lg overflow-hidden">
                      <img
                        src={thumb(card.image_url, 320)}
                        alt={card.title}
                        loading="lazy"
                        decoding="async"
                        className="w-16 h-16 rounded-lg object-cover"
                        style={{
                          objectPosition: `${card.image_focus_x ?? 50}% ${card.image_focus_y ?? 50}%`,
                        }}
                      />
                      {card.nft_address && (
                        <TonSparkles rarity={card.rarity} sizeScale={0.55} />
                      )}
                    </div>
                    {card.nft_address && (
                      <div
                        className="absolute -top-1 -left-1 w-4 h-4 rounded-full flex items-center justify-center shadow-lg"
                        style={{ background: 'linear-gradient(135deg,#0098EA,#0073C7)', boxShadow: '0 0 6px rgba(0,152,234,0.6)' }}
                      >
                        <Sparkles className="w-2 h-2 text-white" />
                      </div>
                    )}
                    {card.is_unique && (
                      <div
                        title={language === 'ru' ? 'Единственный экземпляр' : 'One of a kind'}
                        className="absolute -top-1 -right-1 px-1 rounded text-[7px] font-bold tracking-wider"
                        style={{
                          background: 'linear-gradient(135deg,#FACC15,#F59E0B)',
                          color: '#0a0a12',
                          boxShadow: '0 0 6px rgba(250,204,21,0.5)',
                          letterSpacing: '0.08em',
                          lineHeight: '1.4',
                        }}
                      >
                        U
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#F5F5F7] truncate">{card.title}</p>
                    <p className="text-[11px] text-[#A1A1AA] truncate">{card.artist}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] font-mono font-semibold" style={{ color: rarityColor }}>{label}</span>
                      <span className="text-[10px] text-[#52525B]">@{card.owner_username || (isOwn ? (user?.username || 'you') : '???')}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="h-1 flex-1 rounded-full bg-[#1a1a2e] overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.min(durability, 100)}%`, backgroundColor: durability > 50 ? '#10B981' : durability > 20 ? '#F59E0B' : '#EF4444' }} />
                      </div>
                      <span className="text-[9px] font-mono" style={{ color: durability > 50 ? '#10B981' : durability > 20 ? '#F59E0B' : '#EF4444' }}>{durability.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0 pt-0.5 gap-1">
                    <div className="flex items-center gap-1">
                      <ChessRook className="w-3.5 h-3.5 text-[#D4AF37]" />
                      <span className="font-mono text-sm text-[#D4AF37]">Lv.{level}</span>
                    </div>
                    <div
                      className="flex items-center gap-0.5"
                      title={language === 'ru' ? 'Мин. ценность' : 'Min. value'}
                    >
                      <Star className="w-2.5 h-2.5 text-[#FACC15] fill-[#FACC15] animate-star-pulse" />
                      <span className="font-mono text-[10px] text-[#FACC15]">
                        {Math.max(card.auction_won_amount || 0, level * 10)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedCard && <PaintingPreviewModal card={selectedCard} language={language} t={t} onClose={() => setSelectedCard(null)} />}
    </div>
  );
};

export default TopPage;
